import { ReactElement, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';

import config from '@/config';
import { useDrawerSwipeDirection } from '@shared/lib/hooks';
import { Favorite, removeFavorite, useFavorites } from '@entities/favorite';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@shared/ui/drawer';

import styles from './favorites-drawer.module.css';
import { useT } from '@shared/i18n';

/**
 * The home screen's entry point to the reader's favorites -- the
 * bookmark-style list `IssueButton.Favorite` (`widgets/issue-reader`)
 * writes into. Opened either by a `trigger` element (the template's own
 * button, keeping its styling -- poets' navbar) or, with no trigger, by the
 * caller's own `open`/`onOpenChange` state (basic's `AppMenu` entry, which
 * lives in `shared/` and can't render this widget itself).
 *
 * A right-hand side panel from tablet width up, a bottom sheet below it.
 *
 * Gated on `config.app.favorite`, the same flag the reader button checks --
 * a tenant without favorites gets neither half.
 */
export function FavoritesDrawer({
	trigger,
	open: controlledOpen,
	onOpenChange,
}: FavoritesDrawerProps) {
	const t = useT();
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const open = controlledOpen ?? uncontrolledOpen;
	const swipeDirection = useDrawerSwipeDirection();
	const setOpen = (value: boolean) => {
		setUncontrolledOpen(value);
		onOpenChange?.(value);
	};

	if (!config.app.favorite) {
		return null;
	}

	return (
		<Drawer
			open={open}
			onOpenChange={setOpen}
			swipeDirection={swipeDirection}
		>
			{trigger ? <DrawerTrigger render={trigger} /> : null}
			<DrawerContent className={styles['drawer']}>
				<DrawerHeader className={styles['header']}>
					<DrawerTitle className={styles['title']}>
						{t('common.favorites')}
					</DrawerTitle>
					<DrawerDescription className={styles['description']}>
						{t('favorites.description')}
					</DrawerDescription>
				</DrawerHeader>
				<FavoritesList />
			</DrawerContent>
		</Drawer>
	);
}

interface FavoritesDrawerProps {
	trigger?: ReactElement<Record<string, unknown>>;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

function FavoritesList() {
	const t = useT();
	const favorites = useFavorites();

	if (!favorites.length) {
		return (
			<p className={styles['empty']}>
				{t('favorites.empty')}
			</p>
		);
	}

	// The only scroll container in the drawer -- the header above it is a
	// `shrink-0` sibling, so a long list scrolls under a pinned title and
	// description instead of carrying them away.
	return (
		<ul className={styles['list']}>
			{favorites.map((favorite) => (
				<FavoriteItem key={favorite.path} favorite={favorite} />
			))}
		</ul>
	);
}

function FavoriteItem({ favorite }: { favorite: Favorite }) {
	const t = useT();
	const navigate = useNavigate();
	const { path, title, issueTitle } = favorite;

	return (
		<li className={styles['item']}>
			<button
				type="button"
				className={styles['link']}
				onClick={() => {
					// Deliberately doesn't close the drawer first (no local
					// `setOpen(false)`) -- this is a `push` navigation away
					// from `/`, so `Home` unmounts this drawer's whole tree
					// regardless. Closing it here would, for a caller that
					// syncs open state to a `?dialog=` search param (`basic`'s
					// `Home`), replace that param to cleared *on the current
					// history entry* an instant before the push -- the exact
					// entry Back is supposed to return to with the drawer
					// still open.
					void navigate({ to: path });
				}}
			>
				<span dir="auto" className={styles['item-title']}>{title}</span>
				<span className={styles['item-issue']}>{issueTitle}</span>
			</button>
			<button
				type="button"
				className={styles['remove']}
				aria-label={t('favorites.removeArticle', { title })}
				title={t('favorites.remove')}
				onClick={() => removeFavorite(path)}
			>
				<Trash2 size={18} />
			</button>
		</li>
	);
}
